import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { apartmentsApi, type CreateApartmentData, type Building, type ApartmentGroup, type ApartmentUsageType } from '../../api/apartmentsApi';
import AddApartmentGroupModal from '../../components/modals/AddApartmentGroupModal';
import AddApartmentUsageTypeModal from '../../components/modals/AddApartmentUsageTypeModal';
import { parseDisplayDateToIso } from '../../utils/apartmentDate';
import { isValidEmailList } from '../../utils/emailList';

const AddApartment: React.FC = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [usageTypes, setUsageTypes] = useState<ApartmentUsageType[]>([]);
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [showAddUsageTypeModal, setShowAddUsageTypeModal] = useState(false);
  
  const [formData, setFormData] = useState<CreateApartmentData>({
    code: '',
    name: '',
    occupancyStatus: undefined,
    handoverDate: '',
    peCode: '',
    populationCount: undefined,
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
    invoiceEmail: '',
    address: '',
    area: undefined,
    taxCode: '',
    notes: '',
    hasVatInvoice: false,
    buildingId: '',
    groupId: '',
    usageTypeId: '',
    registeredQuotaCount: undefined,
  });

  useEffect(() => {
    loadBuildings();
  }, []);

  const loadBuildings = async () => {
    try {
      const data = await apartmentsApi.getBuildings();
      setBuildings(data);
    } catch (err) {
      setError(t('failedToLoad'));
    }
  };

  const loadApartmentGroups = async (buildingId: string) => {
    if (!buildingId) {
      setApartmentGroups([]);
      return [];
    }
    try {
      const data = await apartmentsApi.getApartmentGroups(buildingId);
      setApartmentGroups(data);
      return data;
    } catch (err) {
      console.error('Failed to load apartment groups:', err);
      setApartmentGroups([]);
      return [];
    }
  };

  const loadUsageTypes = async (buildingId?: string) => {
    if (!buildingId) {
      setUsageTypes([]);
      return;
    }
    try {
      const data = await apartmentsApi.getApartmentUsageTypes(buildingId);
      setUsageTypes(data);
      const defaultType = data.find(type => type.isDefault);
      if (defaultType) {
        setFormData(prev => ({ ...prev, usageTypeId: defaultType.id }));
      }
    } catch (err) {
      console.error('Failed to load usage types:', err);
    }
  };

  const handleGroupCreated = () => {
    if (formData.buildingId) {
      loadApartmentGroups(formData.buildingId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.code.trim()) {
      setError(t('pleaseEnterApartmentCode'));
      return;
    }
    if (!formData.ownerName.trim()) {
      setError(t('pleaseEnterOwnerName'));
      return;
    }
    if (!formData.buildingId) {
      setError(t('pleaseSelectBuilding'));
      return;
    }
    if (!isValidEmailList(formData.ownerEmail)) {
      setError(t('invalidEmailList'));
      return;
    }
    if (!isValidEmailList(formData.invoiceEmail)) {
      setError(t('invalidEmailList'));
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        groupId: formData.groupId || undefined,
        usageTypeId: formData.usageTypeId || undefined,
        area: formData.area || undefined,
        registeredQuotaCount: formData.registeredQuotaCount ?? undefined,
        populationCount: formData.populationCount ?? undefined,
        occupancyStatus: formData.occupancyStatus || undefined,
        handoverDate: parseDisplayDateToIso(formData.handoverDate),
        name: formData.name?.trim() || undefined,
        peCode: formData.peCode?.trim() || undefined,
      };
      await apartmentsApi.createApartment(submitData);
      alert(t('apartmentCreatedSuccess'));
      navigate('/apartments');
    } catch (err) {
      setError(t('errorCreatingApartment'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked 
               : name === 'area' ? (value ? parseFloat(value) : undefined)
               : name === 'registeredQuotaCount' || name === 'populationCount'
                 ? (value ? parseInt(value, 10) : undefined)
               : value
      };
      
      // When building changes, auto-select default group if available
      if (name === 'buildingId' && value) {
        void loadApartmentGroups(value).then((groups) => {
          const defaultGroup = groups.find((group) => group.name === 'Nhóm mặc định');
          setFormData((prev) => ({
            ...prev,
            groupId: defaultGroup?.id || '',
          }));
        });
        loadUsageTypes(value);
      } else if (name === 'buildingId' && !value) {
        setApartmentGroups([]);
        newData.groupId = '';
      }
      
      return newData;
    });
  };

  const filteredUsageTypes = usageTypes.filter(type =>
    !formData.buildingId || type.buildingId === formData.buildingId
  ).sort((a, b) => {
    if (a.isDefault) return -1;
    if (b.isDefault) return 1;
    return a.name.localeCompare(b.name);
  });

  const filteredGroups = formData.buildingId
    ? [...apartmentGroups].sort((a, b) => {
      if (a.name === 'Nhóm mặc định') return -1;
      if (b.name === 'Nhóm mặc định') return 1;
      return a.name.localeCompare(b.name);
    })
    : [];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('addApartment')}</h1>
        <button
          onClick={() => navigate('/apartments')}
          className="text-gray-600 hover:text-gray-800 transition-colors"
        >
          {t('back')}
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - General Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
              {t('generalInfo')}
            </h3>
            
            <div className="space-y-4">
              {/* Building Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectBuilding')}
                </label>
                <select
                  name="buildingId"
                  value={formData.buildingId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">{t('selectBuildingPlaceholder')}</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Apartment Group Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectApartmentGroup')}
                </label>
                <select
                  name="groupId"
                  value={formData.groupId}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('noApartmentGroup')}</option>
                  {filteredGroups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddGroupModal(true)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 border border-red-300 px-3 py-1 rounded"
                >
                  + {t('addApartmentGroup')}
                </button>
              </div>

              {/* Apartment Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentCode')}
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder={t('apartmentCodePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Apartment Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentName')}
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name || ''}
                  onChange={handleChange}
                  placeholder={t('apartmentNamePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Occupancy Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentOccupancyStatus')}
                </label>
                <select
                  name="occupancyStatus"
                  value={formData.occupancyStatus || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">--</option>
                  <option value="OWNER_USING">{t('occupancyOwnerUsing')}</option>
                  <option value="RENTED">{t('occupancyRented')}</option>
                  <option value="FOR_RENT">{t('occupancyForRent')}</option>
                  <option value="FOR_SALE">{t('occupancyForSale')}</option>
                </select>
              </div>

              {/* Handover Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('handoverDate')}
                </label>
                <input
                  type="text"
                  name="handoverDate"
                  value={formData.handoverDate || ''}
                  onChange={handleChange}
                  placeholder={t('handoverDatePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('handoverDateEmptyHint')}</p>
              </div>

              {/* PE Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('peCode')}
                </label>
                <input
                  type="text"
                  name="peCode"
                  value={formData.peCode || ''}
                  onChange={handleChange}
                  placeholder={t('peCodePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Population Count */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('populationCount')}
                </label>
                <input
                  type="number"
                  name="populationCount"
                  value={formData.populationCount ?? ''}
                  onChange={handleChange}
                  placeholder={t('populationCountPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="1"
                />
              </div>

              {/* Usage Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('usageType')}
                </label>
                <select
                  name="usageTypeId"
                  value={formData.usageTypeId || ''}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('noUsageType')}</option>
                  {filteredUsageTypes.map((type) => (
                    <option key={type.id} value={type.id}>
                      {type.name} ({type.code})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddUsageTypeModal(true)}
                  className="mt-2 text-sm text-red-600 hover:text-red-800 border border-red-300 px-3 py-1 rounded"
                >
                  + {t('addApartmentUsageType')}
                </button>
              </div>

              {/* Registered Quota Count */}
              {/* Area */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentArea')}
                </label>
                <input
                  type="number"
                  name="area"
                  value={formData.area || ''}
                  onChange={handleChange}
                  placeholder={t('apartmentAreaPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Right Column - Owner Information */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-4 text-blue-600 border-b border-blue-200 pb-2">
              {t('ownerInfoTitle')}
            </h3>
            
            <div className="space-y-4">
              {/* Owner Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('ownerName')}
                </label>
                <input
                  type="text"
                  name="ownerName"
                  value={formData.ownerName}
                  onChange={handleChange}
                  placeholder={t('ownerNamePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('ownerPhone')}
                </label>
                <input
                  type="tel"
                  name="ownerPhone"
                  value={formData.ownerPhone}
                  onChange={handleChange}
                  placeholder={t('ownerPhonePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentAddress')}
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('ownerAddressPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email - Fee notification */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('feeNotificationEmail')}
                </label>
                <input
                  type="text"
                  name="ownerEmail"
                  value={formData.ownerEmail}
                  onChange={handleChange}
                  placeholder={t('feeNotificationEmailPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
              </div>

              {/* Invoice Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('invoiceEmail')}
                </label>
                <input
                  type="text"
                  name="invoiceEmail"
                  value={formData.invoiceEmail}
                  onChange={handleChange}
                  placeholder={t('invoiceEmailPlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-500">{t('multipleEmailsHint')}</p>
              </div>

              {/* Tax Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('taxCode')}
                </label>
                <input
                  type="text"
                  name="taxCode"
                  value={formData.taxCode}
                  onChange={handleChange}
                  placeholder={t('taxCodePlaceholder')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('apartmentNotes')}
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder={t('notesPlaceholder')}
                  rows={3}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* VAT Invoice Checkbox */}
              <div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="hasVatInvoice"
                    checked={formData.hasVatInvoice}
                    onChange={handleChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-700">
                    {t('hasVatInvoice')}
                  </label>
                </div>
                <p className="mt-2 text-xs text-red-600">
                  {t('vatInvoiceNote')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex justify-center">
          <button
            type="submit"
            disabled={loading}
            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-8 rounded-lg transition-colors disabled:opacity-50 flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t('saving') : t('saveInfo')}
          </button>
        </div>
      </form>

      <AddApartmentGroupModal
        isOpen={showAddGroupModal}
        onClose={() => setShowAddGroupModal(false)}
        onSuccess={handleGroupCreated}
        selectedBuildingId={formData.buildingId}
      />
      <AddApartmentUsageTypeModal
        isOpen={showAddUsageTypeModal}
        onClose={() => setShowAddUsageTypeModal(false)}
        onSuccess={() => loadUsageTypes(formData.buildingId)}
        selectedBuildingId={formData.buildingId}
      />
    </div>
  );
};

export default AddApartment; 